import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  createPortfolio,
  deleteCompanyPortfolioFile,
  deletePortfolio,
  deleteUserPortfolioFile,
  getCompanyPortfoliosFilesIds,
  getPortfolios,
  getUserPortfoliosFilesIds,
  updatePortfolio,
  uploadCompanyPortfolioFiles,
  uploadUserPortfolioFiles,
} from '@app/api';
import { Portfolio } from '@app/models';
import Loader from '../Loader';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { IconCloseMark, IconEdit16, IconPlusCircle24 } from '@app/icons';
import FilePicker from '../FilePicker';
import { ExpandablePaper } from '@app/common';
import { PortfolioForm } from '@app/forms';
import { useNotification, useUserContext } from '@app/providers';
import { calculatePortfolios, dateFormat } from '@app/helpers';
import { cn } from 'lib/utils';

interface PortfolioListProps {
  companyId?: string;
  userId?: string;
  editable?: boolean;
  className?: string;
}

interface StyledPortfolioProps {
  portfolio: Portfolio;
  editable?: boolean;
  onEdit?: (portfolio: Portfolio) => void;
  onDelete?: (portfolio: Portfolio) => void;
}

function StyledPortfolio(props: StyledPortfolioProps) {
  const {
    portfolio,
    editable,
    onEdit: onEditProps,
    onDelete: onDeleteProps,
  } = props;
  const { name, description, startDate, endDate } = portfolio;
  const [expanded, setExpanded] = useState<boolean>(false);

  const onChange = useCallback(() => {
    setExpanded((prevExpanded) => !prevExpanded);
  }, []);

  const onEdit = useCallback(() => {
    if (onEditProps) {
      onEditProps(portfolio);
    }
  }, [onEditProps, portfolio]);

  const onClickDelete = useCallback(() => {
    if (onDeleteProps) {
      onDeleteProps(portfolio);
    }
  }, [onDeleteProps, portfolio]);

  return (
    <div className="mb-4">
      <ExpandablePaper
        title={name}
        onChange={onChange}
        expanded={expanded}
        description={`${dateFormat(startDate, 'dd.MM.yyyy')} - ${dateFormat(
          endDate,
          'dd.MM.yyyy'
        )}`}
      >
        {editable && (
          <div className="w-full flex justify-end gap-2 mb-4">
            <Button
              onClick={onEdit}
              size="sm"
              variant="outline"
              className="flex items-center gap-2"
            >
              <IconEdit16 className="w-4 h-4" />
              <span className="hidden sm:inline">Редактировать</span>
              <span className="sm:hidden">Изменить</span>
            </Button>
            <Button
              onClick={onClickDelete}
              size="sm"
              variant="outline"
              className="flex items-center gap-2 text-muted-foreground hover:text-destructive hover:border-destructive/50 hover:bg-destructive/5 transition-colors"
            >
              <IconCloseMark />
              <span className="hidden sm:inline">Удалить</span>
            </Button>
          </div>
        )}
        <div className="text-sm text-muted-foreground leading-relaxed break-words whitespace-pre-wrap overflow-hidden">
          {description}
        </div>
      </ExpandablePaper>
    </div>
  );
}

function PortfolioList(props: PortfolioListProps) {
  const { companyId, userId, editable = true, className } = props;
  const { showNotification } = useNotification();
  const { currentProfile } = useUserContext();
  const [pending, setPending] = useState<boolean>(true);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [updatingPortfolio, setUpdatingPortfolio] = useState<boolean>(false);
  const [formVisible, setFormVisible] = useState<boolean>(false);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [editingPortfolio, setEditingPortfolio] = useState<Portfolio | null>(
    null
  );
  const [fileIds, setFileIds] = useState<string[]>([]);

  const onClickShowForm = useCallback(() => {
    setFormVisible(true);
  }, []);

  const onClickEdit = useCallback((portfolio: Portfolio) => {
    setFormVisible(true);
    setEditingPortfolio(portfolio);
  }, []);

  const onDeletePortfolio = useCallback(
    async (portfolio: Portfolio) => {
      try {
        await deletePortfolio(portfolio.id);
        setPortfolios((prev) => prev.filter((p) => p.id !== portfolio.id));
        showNotification({
          variant: 'success',
          message: 'Опыт работы удалён',
        });
      } catch (e) {
        showNotification({
          variant: 'error',
          message: 'Не удалось удалить опыт работы',
        });
      }
    },
    [showNotification]
  );

  const onClickHiddenForm = useCallback(() => {
    setFormVisible(false);
    setEditingPortfolio(null);
  }, []);

  const onSubmitForm = useCallback(
    async (portfolio: Partial<Portfolio>) => {
      try {
        setUpdatingPortfolio(true);

        if (companyId) {
          portfolio.companyId = companyId;
        } else {
          portfolio.userId = userId;
        }

        if (editingPortfolio) {
          const response = await updatePortfolio(portfolio);
          setPortfolios((prevPortfolios) => {
            const index = prevPortfolios.findIndex(
              (item) => item.id === response.id
            );
            const data = [...prevPortfolios];
            if (index !== -1) data[index] = response;
            return data;
          });
        } else {
          const response = await createPortfolio(portfolio);
          setPortfolios((prevPortfolios) => [response, ...prevPortfolios]);
        }

        setFormVisible(false);
        setEditingPortfolio(null);
        setUpdatingPortfolio(false);

        showNotification({
          variant: 'success',
          message: 'Опыт работы был успешно добавлен',
        });
      } catch (e) {
        setUpdatingPortfolio(false);

        showNotification({
          variant: 'error',
          message: 'Не удалось добавить опыт работы',
        });
      }
    },
    [companyId, editingPortfolio, showNotification, userId]
  );

  const onChangeFiles = useCallback(
    async (files: File[]) => {
      try {
        let response;

        setUploadProgress(0);

        if (companyId) {
          response = await uploadCompanyPortfolioFiles(
            companyId,
            files,
            (progress) => setUploadProgress(progress)
          );
        } else {
          response = await uploadUserPortfolioFiles(
            userId!,
            files,
            (progress) => setUploadProgress(progress)
          );
        }

        setFileIds(response);
        showNotification({
          variant: 'success',
          message: 'Файлы успешно загружены',
        });
      } catch (e) {
        showNotification({
          variant: 'error',
          message: 'Не удалось загрузить файлы',
        });
      } finally {
        setUploadProgress(null);
      }
    },
    [companyId, showNotification, userId]
  );

  const getData = useCallback(async () => {
    try {
      let portfoliosFileIdsResponse;
      const params: Partial<Portfolio> = {};

      if (companyId) {
        params.companyId = companyId;
      } else {
        params.userId = userId;
      }

      const portfoliosResponse = await getPortfolios(params);

      if (companyId) {
        portfoliosFileIdsResponse = await getCompanyPortfoliosFilesIds(
          companyId
        );
      } else {
        portfoliosFileIdsResponse = await getUserPortfoliosFilesIds(userId!);
      }

      setPortfolios(portfoliosResponse);
      setFileIds(portfoliosFileIdsResponse);
      setPending(false);
    } catch (e) {
      setPending(false);
    }
  }, [companyId, userId]);

  const onDeleteFile = useCallback(
    async (fileId: string) => {
      try {
        setIsDeleting(true);

        if (companyId) {
          await deleteCompanyPortfolioFile(companyId, fileId);
        } else {
          await deleteUserPortfolioFile(userId!, fileId);
        }

        setFileIds((prevFileIds) =>
          prevFileIds.filter((item) => item !== fileId)
        );
        showNotification({
          variant: 'success',
          message: 'Успешно удалено',
        });
        return true;
      } catch (e) {
        showNotification({
          variant: 'error',
          message: 'Не удалось удалить файл',
        });
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [companyId, userId, showNotification]
  );

  const portfolioDate = useMemo(() => {
    return calculatePortfolios(portfolios);
  }, [portfolios]);

  useEffect(() => {
    getData();
  }, [getData]);

  if (pending) {
    return (
      <div className="w-full flex justify-center py-8">
        <Loader />
      </div>
    );
  }

  return (
    <div className={cn('w-full space-y-4', className)}>
      <Card className="w-full">
        <div className="p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-medium text-foreground mb-4">
            Портфолио
          </h2>
          <FilePicker
            loadableFiles={fileIds}
            onChange={onChangeFiles}
            editable={editable}
            onDelete={onDeleteFile}
            uploadProgress={uploadProgress}
            isDeleting={isDeleting}
          />
        </div>
      </Card>

      <Card className="w-full">
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <h2 className="text-lg sm:text-xl font-medium text-foreground">
              Опыт работы
              {portfolios.length > 0 && currentProfile.currentType === 'user'
                ? `: ${portfolioDate}`
                : ''}
            </h2>
            {editable && (
              <Button
                onClick={onClickShowForm}
                size="sm"
                className="flex items-center gap-2 w-full sm:w-auto justify-center"
              >
                <IconPlusCircle24 className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {editingPortfolio
                    ? 'Обновить опыт работы'
                    : 'Добавить опыт работы'}
                </span>
                <span className="sm:hidden">
                  {editingPortfolio ? 'Обновить' : 'Добавить'}
                </span>
              </Button>
            )}
          </div>

          {formVisible && (
            <div className="mb-6 p-4 bg-muted/50 rounded-lg">
              <PortfolioForm
                onSubmit={onSubmitForm}
                onCancel={onClickHiddenForm}
                pending={updatingPortfolio}
                portfolio={editingPortfolio}
              />
            </div>
          )}

          <div className="space-y-4">
            {portfolios
              .filter((portfolio) => !portfolio.filesIds)
              .map((portfolio) => (
                <StyledPortfolio
                  key={portfolio.id}
                  portfolio={portfolio}
                  editable={editable}
                  onEdit={onClickEdit}
                  onDelete={onDeletePortfolio}
                />
              ))}

            {portfolios.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm sm:text-base">Опыт работы не добавлен</p>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

export default memo(PortfolioList);
